import { IconSearchStroked } from '@consta/icons/IconSearchStroked'
import { Button } from '@consta/uikit/Button'
import { Responses500 } from '@consta/uikit/Responses500'
import { TextField } from '@consta/uikit/TextField'
import {
  searchFromDbSources,
  selectActiveProjectName,
  selectOverallSearchState,
  selectSearchTotalResults,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { CrmsSearchQuery, GeoDBSearchQuery, ImportDBResultViewMode } from '@core/core/types'
import { getImportGeobdSearchParams, getStringFromNumbersLettersOnly } from '@core/core/utils'
import type { Nullable } from 'common/types'
import type { FC, KeyboardEvent } from 'react'
import { useState } from 'react'

import {
  DBSearchFiltersCrms,
  DBSearchFiltersGeoDb,
  DBSearchFiltersGeomate,
} from './DBSearchFilters/'
import { NewImportLoader } from './NewImportLoader'
import css from './NewImportResults.css'
import { ResponsesNothingFound } from './ResponsesNothingFound'
import { SearchResultsContent } from './SearchResultsContent'
import { SearchResultsPanel } from './SearchResultsPanel'
import type { NewImportResultsProps, SearchModeComboItem } from './types'
import { searchModeComboItems } from './types'

export const NewImportResults: FC<NewImportResultsProps> = ({
  projectId,
  mode,
  globalSearch,
  activeQuery,
  addCrmsFiles,
  addGeoDFiles,
}) => {
  const dispatch = useAppDispatch()
  const searchState = useAppSelector(selectOverallSearchState)
  const [selectedMode] = useState<SearchModeComboItem>(
    searchModeComboItems.find(item => item.mode === mode) ?? searchModeComboItems[0],
  )
  const [searchQuery, setSearchQuery] = useState<string>(globalSearch)
  const [showAllFilters] = useState(false)
  const activeGeodbQuery = activeQuery.geodb
  const activeCrmsQuery = activeQuery.crms
  const [selectedCrmsQuery, setSelectedCrmsQuery] = useState<CrmsSearchQuery>(activeCrmsQuery ?? {})
  const [selectedGeoDBQuery, setSelectedGeoDBQuery] = useState<GeoDBSearchQuery>(
    activeGeodbQuery ?? {},
  )

  const activeProjectName = useAppSelector(selectActiveProjectName)
  const foundNumber = useAppSelector(selectSearchTotalResults)

  const canGeodb =
    !!selectedGeoDBQuery.company || !!selectedGeoDBQuery.field || !!selectedGeoDBQuery.licenseArea
  const canCrms =
    !!searchQuery && // црмс не ищет без глобального поиска
    (!!selectedCrmsQuery.endYear ||
      !!selectedCrmsQuery.executer ||
      !!selectedCrmsQuery.identifier ||
      !!selectedCrmsQuery.startYear ||
      !!selectedCrmsQuery.territory)
  const selectedModeValue = selectedMode.mode
  const canSearch =
    searchQuery.length > 1 ||
    (selectedModeValue === 'geodb' || selectedModeValue === 'alldb' ? canGeodb : false) ||
    (selectedModeValue === 'crms' || selectedModeValue === 'alldb' ? canCrms : false)
  const renderFilterSwitch = (mode: ImportDBResultViewMode) => {
    switch (mode) {
      case 'geodb':
        return (
          <DBSearchFiltersGeoDb
            query={selectedGeoDBQuery}
            companyChanged={companyChanged}
            fieldChanged={fieldChanged}
            licenseAreaChanged={licenseAreaChanged}
          />
        )
      case 'crms':
        return (
          <DBSearchFiltersCrms
            query={selectedCrmsQuery}
            territoryChanged={territoryChanged}
            startYearChanged={startYearChanged}
            endYearChanged={endYearChanged}
            projectIdChanged={projectIdChanged}
            executorChanged={executorChanged}
          />
        )
      case 'geomate':
        return <DBSearchFiltersGeomate />
      case 'alldb':
        return (
          <>
            <DBSearchFiltersGeoDb
              query={selectedGeoDBQuery}
              companyChanged={companyChanged}
              fieldChanged={fieldChanged}
              licenseAreaChanged={licenseAreaChanged}
            />
            {!!showAllFilters && (
              <>
                <DBSearchFiltersCrms
                  query={selectedCrmsQuery}
                  territoryChanged={territoryChanged}
                  startYearChanged={startYearChanged}
                  endYearChanged={endYearChanged}
                  projectIdChanged={projectIdChanged}
                  executorChanged={executorChanged}
                />
                <DBSearchFiltersGeomate />
              </>
            )}
          </>
        )
    }
  }

  const renderImportResult = (mode: ImportDBResultViewMode) => {
    if (searchState === 'loading') {
      return (
        <div className={css.stateLoading}>
          <NewImportLoader />
        </div>
      )
    }
    switch (mode) {
      case 'geodb':
      case 'geomate':
      case 'alldb':
        return foundNumber ? (
          <>
            <SearchResultsPanel currentMode={currentMode} foundNumber={foundNumber} />
            <SearchResultsContent
              projectId={projectId}
              addCrmsFiles={addCrmsFiles}
              addGeoDFiles={addGeoDFiles}
            />
          </>
        ) : (
          <div className={css.notFound}>
            <ResponsesNothingFound />
          </div>
        )
      case 'crms':
        return foundNumber ? (
          <>
            <SearchResultsPanel currentMode={currentMode} foundNumber={foundNumber} />
            <SearchResultsContent
              projectId={projectId}
              addCrmsFiles={addCrmsFiles}
              addGeoDFiles={addGeoDFiles}
            />
          </>
        ) : (
          searchState === 'failed' && (
            <div className={css.serverError}>
              <Responses500
                title="БД ЦРМС недоступна для поиска"
                description="Попробуйте обновить страницу или зайти позже"
              />
            </div>
          )
        )
    }
  }

  // const onModeChange = (props: { value: Nullable<SearchModeComboItem>; e: SyntheticEvent }) => {
  //   setSelectedMode(props.value ?? searchModeComboItems[0])
  // }
  const onGlobalSearchChange = (value: Nullable<string>) => {
    setSearchQuery(getStringFromNumbersLettersOnly(value))
  }

  const territoryChanged = (value: Nullable<string>) => {
    setSelectedCrmsQuery({ ...selectedCrmsQuery, territory: value ?? undefined })
  }

  const projectIdChanged = (value: Nullable<string>) => {
    setSelectedCrmsQuery({ ...selectedCrmsQuery, identifier: value ?? undefined })
  }

  const executorChanged = (value: Nullable<string>) => {
    setSelectedCrmsQuery({ ...selectedCrmsQuery, executer: value ?? undefined })
  }

  const startYearChanged = (value: number | undefined) => {
    setSelectedCrmsQuery({
      ...selectedCrmsQuery,
      startYear: value,
    })
  }

  const companyChanged = (value: string | undefined) => {
    setSelectedGeoDBQuery({
      ...selectedGeoDBQuery,
      company: value,
      field: undefined,
      licenseArea: undefined,
    })
  }

  const fieldChanged = (value: string | undefined) => {
    setSelectedGeoDBQuery({
      ...selectedGeoDBQuery,
      field: value,
    })
  }

  const licenseAreaChanged = (value: string | undefined) => {
    setSelectedGeoDBQuery({
      ...selectedGeoDBQuery,
      licenseArea: value,
    })
  }

  const endYearChanged = (value: number | undefined) => {
    setSelectedCrmsQuery({
      ...selectedCrmsQuery,
      endYear: value,
    })
  }

  const currentMode = selectedMode.mode
  const applyFilters = () => {
    const data = {
      query: {
        mode: currentMode,
        globalSearch: searchQuery,
        crms: selectedCrmsQuery,
        geodb: selectedGeoDBQuery,
      },
    }
    if (data.query.globalSearch.length > 1) dispatch(searchFromDbSources(data))
  }

  const clearFilters = () => {
    setSelectedCrmsQuery({})
    setSelectedGeoDBQuery({})
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      applyFilters()
    }
  }

  return (
    <div className={css.container} onKeyDown={handleKeyDown}>
      <div className={css.wrapper}>
        {/* TODO Раскомментировать, когда будет готов функционал
        <Select
          size="s"
          items={searchModeComboItems}
          value={selectedMode}
          getItemKey={i => i.mode}
          getItemLabel={i => i.label}
          required={true}
          style={{ width: '20rem' }}
          form="defaultClear"
          data-testid="searchMode"
          onChange={onModeChange}
        /> */}
        <TextField
          className={css.text}
          size="s"
          value={searchQuery}
          leftSide={IconSearchStroked}
          withClearButton={true}
          onChange={onGlobalSearchChange}
        />
      </div>
      <div className={css.filter}>{renderFilterSwitch(selectedMode.mode)}</div>
      <div className={css.buttonsRow}>
        <Button
          size="s"
          view="primary"
          label="Показать результаты"
          data-analytics-id="new import_geobd_search"
          data-analytic-params={getImportGeobdSearchParams(
            Object.values(selectedGeoDBQuery),
            activeProjectName,
          )}
          disabled={!canSearch}
          onClick={applyFilters}
        />
        {selectedModeValue === 'alldb' && (
          <>
            {/* TODO Раскомментировать, когда будет готов функционал
             <Collapse
              className={`${css.searchCollapseBody} m-h-3`}
              label={!showAllFilters ? 'Показать все фильтры' : 'Свернуть'}
              size="s"
              isOpen={showAllFilters}
              iconPosition="right"
              onClick={() => setShowAllFilters(!showAllFilters)}
            /> */}
            <Button
              className={css.resetFilter}
              size="s"
              view="ghost"
              label="Сбросить фильтры"
              onClick={clearFilters}
            />
          </>
        )}
      </div>
      {renderImportResult(selectedMode.mode)}
    </div>
  )
}
